import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightSvg } from '~/svg';
import {
    deleteUnmonitoredChange,
    fetchUnmonitoredChanges,
    UnmonitoredChange,
} from '~/services/unmonitored-changes';
import toast from 'react-hot-toast';

const TrashIcon = () => (
    <svg className='fill-current' xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
        <path d='M7 21C6.45 21 5.97917 20.8042 5.5875 20.4125C5.19583 20.0208 5 19.55 5 19V6H4V4H9V3H15V4H20V6H19V19C19 19.55 18.8042 20.0208 18.4125 20.4125C18.0208 20.8042 17.55 21 17 21H7ZM17 6H7V19H17V6ZM9 17H11V8H9V17ZM13 17H15V8H13V17Z' fill='' />
    </svg>
);

const GuestBookIcon = () => (
    <svg className='fill-current' xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none'>
        <path d='M12 20C11.2 19.3667 10.3333 18.875 9.4 18.525C8.46667 18.175 7.5 18 6.5 18C5.8 18 5.1125 18.0917 4.4375 18.275C3.7625 18.4583 3.11667 18.7167 2.5 19.05C2.15 19.2333 1.8125 19.225 1.4875 19.025C1.1625 18.825 1 18.5333 1 18.15V6.1C1 5.91667 1.04583 5.74167 1.1375 5.575C1.22917 5.40833 1.36667 5.28333 1.55 5.2C2.31667 4.8 3.11667 4.5 3.95 4.3C4.78333 4.1 5.63333 4 6.5 4C7.46667 4 8.4125 4.125 9.3375 4.375C10.2625 4.625 11.15 5 12 5.5V17.6C12.85 17.0667 13.7417 16.6667 14.675 16.4C15.6083 16.1333 16.55 16 17.5 16C18.1 16 18.6875 16.05 19.2625 16.15C19.8375 16.25 20.4167 16.4 21 16.6V4.6C21.25 4.68333 21.4958 4.77083 21.7375 4.8625C21.9792 4.95417 22.2167 5.06667 22.45 5.2C22.6333 5.28333 22.7708 5.40833 22.8625 5.575C22.9542 5.74167 23 5.91667 23 6.1V18.15C23 18.5333 22.8375 18.825 22.5125 19.025C22.1875 19.225 21.85 19.2333 21.5 19.05C20.8833 18.7167 20.2375 18.4583 19.5625 18.275C18.8875 18.0917 18.2 18 17.5 18C16.5 18 15.5333 18.175 14.6 18.525C13.6667 18.875 12.8 19.3667 12 20ZM14 15V5.5L19 0.5V10.5L14 15ZM10 16.625V6.725C9.45 6.49167 8.87917 6.3125 8.2875 6.1875C7.69583 6.0625 7.1 6 6.5 6C5.88333 6 5.28333 6.05833 4.7 6.175C4.11667 6.29167 3.55 6.46667 3 6.7V16.625C3.58333 16.4083 4.1625 16.25 4.7375 16.15C5.3125 16.05 5.9 16 6.5 16C7.1 16 7.6875 16.05 8.2625 16.15C8.8375 16.25 9.41667 16.4083 10 16.625Z' fill='' />
    </svg>
);

const formatDate = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleDateString('en-US', {
        month: '2-digit',
        day: '2-digit',
        year: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
    });
};

interface CardProps {
    entry: UnmonitoredChange;
    onDelete: (entry: UnmonitoredChange) => void;
    busy: boolean;
}

const EntryCard = ({ entry, onDelete, busy }: CardProps) => (
    <div className='col-span-12 xl:col-span-4'>
        <div className='mb-4 flex h-full flex-col justify-between rounded-sm border border-stroke bg-white p-4 shadow-default dark:border-strokedark dark:bg-boxdark md:mb-6 md:p-6 xl:p-7.5 2xl:mb-7.5'>
            <div>
                <div className='eyebrow mb-3 flex gap-2'>
                    <GuestBookIcon />
                    Guest Book
                </div>

                <div className='mb-4 flex justify-between'>
                    <span className='text-primary'>{entry.campaign_person_name}</span>
                    <span className='text-sm text-bodydark'>{formatDate(entry.created_at)}</span>
                </div>

                <p className='line-clamp-6 text-sm'>{entry.body}</p>
                {entry.signature && (
                    <p className='mt-2 text-sm italic text-bodydark'>— {entry.signature}</p>
                )}
            </div>

            <div className='mt-4 flex gap-4'>
                <Link
                    to='/un-monitored-changes'
                    className='flex gap-1 text-primary'
                >
                    <ArrowRightSvg />
                    View All
                </Link>
                <button
                    onClick={() => onDelete(entry)}
                    disabled={busy}
                    className='flex gap-1 text-danger disabled:opacity-50'
                >
                    <TrashIcon />
                    Delete
                </button>
            </div>
        </div>
    </div>
);

const UnmonitoredChanges = () => {
    const [entries, setEntries] = useState<UnmonitoredChange[]>([]);
    const [total, setTotal] = useState<number | null>(null);
    const [loading, setLoading] = useState(true);
    const [busyIds, setBusyIds] = useState<Set<number>>(new Set());


    useEffect(() => {
        fetchUnmonitoredChanges({ type: 'guest_book', page: 1, page_size: 3 })
            .then((data) => {
                setEntries(data.results.slice(0, 3));
                setTotal(data.counts.guest_book);
            })
            .catch(() => {
                // non-fatal — card stays hidden on error
            })
            .finally(() => setLoading(false));
    }, []);

    const handleDelete = async (entry: UnmonitoredChange) => {
        setBusyIds((prev) => new Set(prev).add(entry.id));
        try {
            await deleteUnmonitoredChange(entry);
            setEntries((prev) => prev.filter((e) => e.id !== entry.id));
            setTotal((prev) => (prev !== null ? Math.max(0, prev - 1) : prev));
            toast.success('Entry deleted');
        } catch {
            toast.error('Failed to delete entry');
        } finally {
            setBusyIds((prev) => {
                const next = new Set(prev);
                next.delete(entry.id);
                return next;
            });
        }
    };

    if (loading || entries.length === 0) return null;

    return (
        <section className='unmonitored-changes mb-7.5'>
            <div className='mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                <h2 className='text-xl font-semibold text-black dark:text-white'>
                    Unmonitored Changes{total !== null ? ` (${total})` : ''}
                </h2>

                <Link
                    to='/un-monitored-changes'
                    className='flex items-center gap-2 text-primary'
                >
                    View All <ArrowRightSvg />
                </Link>
            </div>

            <div className='mb-7.5 grid grid-cols-12 gap-7.5'>
                {entries.map((entry) => (
                    <EntryCard
                        key={entry.id}
                        entry={entry}
                        onDelete={handleDelete}
                        busy={busyIds.has(entry.id)}
                    />
                ))}
            </div>
        </section>
    );
};

export default UnmonitoredChanges;
